/* eslint-disable @typescript-eslint/no-explicit-any */
// WebSocket-driven replacement for the legacy polling conversation web worker.
// Owns the ACTIVE conversation's messages: subscribes to #message#{convId} and
// merges the changed item on each event, plus optimistic send echo and
// safety-net refetches. The sidebar conversation LIST is sourced from
// useGetConversations (TanStack invalidate-on-#conversation# + 60s poll),
// unioned with a render-time optimistic overlay for just-created rows
// (ENG-4382), and re-exported here for a byte-stable public API.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { reconcileMessages } from '@/hooks/chatMessageReconcile';
import { prependConversationToCache } from '@/hooks/chatWorkerCache';
import { useAxios } from '@/hooks/useAxios';
import { useGetConversations } from '@/hooks/useChat';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import {
  useWebSocket,
  useWsLiveness,
  type WebSocketMessage,
} from '@/hooks/useWebSocket';
import { useAuth } from '@/state/auth';
import type { Conversation, Message } from '@/types';

interface PendingMessage {
  id: string;
  text: string;
  timestamp: number;
}

interface ChatWorkerState {
  conversations: Conversation[];
  messages: Message[];
  pendingMessages: PendingMessage[];
  conversationsLoading: boolean;
  // ENG-4398: offset-pagination pass-through from useGetConversations. The
  // sidebar list GET returns one DynamoDB response page; when the backend
  // echoes an `offset` there are more conversations beyond the snapshot.
  hasMoreConversations: boolean;
  isLoadingMoreConversations: boolean;
  loadMoreConversations: () => void;
  messagesLoading: boolean;
  error: string | null;
  sendMessage: (
    conversationId: string,
    message: string,
    mode?: 'query' | 'agent'
  ) => Promise<void>;
  createConversation: (
    message: string,
    mode?: 'query' | 'agent'
  ) => Promise<any>;
  compactConversation: (sourceConversationId: string) => Promise<any>;
  stopConversation: (conversationId: string) => Promise<void>;
  refetchMessages: (convId: string) => Promise<void>;
}

const PENDING_TIMEOUT_MS = 30_000;

// ENG-4382: how long a create/compact response stays in the render-time
// overlay when the authoritative row never lands (missed WS invalidate AND
// absent from every poll). Long enough to bridge parent_id:self GSI lag plus
// the 60s fallback poll; short enough that a never-landing create can't leave
// a permanent ghost row.
const OVERLAY_TTL_MS = 90_000;

// ENG-4506: message-poll fallback cadences. Fallback mode (no WS URL, or the
// socket has NEVER connected — dev parity with the pre-April-2026 polling
// worker) polls the open conversation at 3s while it looks running; when it
// looks idle, only every third tick fetches (9s — inside the ticket's ≤10s
// bound; gated at 7.5s so tick-lateness jitter can't slide the fetch to the
// 12s tick). Safety-net mode
// (WS configured and connected at least once) polls at 60s ONLY while the
// conversation looks running — the zombie-socket belt-and-braces, run
// unconditionally on current socket state per the FALLBACK_POLL_MS philosophy
// in useChat.ts.
const NO_WS_MESSAGES_ACTIVE_POLL_MS = 3_000;
const NO_WS_MESSAGES_IDLE_POLL_MS = 9_000;
// Idle-fetch gate: the every-3rd-tick check compares against the midpoint of
// the safe interval (IDLE − ACTIVE/2 = 7.5s) rather than the raw 9s cadence.
// Chromium keeps repeating timers on a fixed aligned cadence, so a fetch tick
// that RUNS late does not shift subsequent ticks: the 3rd-following tick then
// measures 9000 − lateness and a strict <9000 check skips it, sliding the
// idle fetch to ~12s — past the ticket's ≤10s bound. 7.5s tolerates 1.5s of
// tick lateness; on an exact clock the 2nd tick (6s) still skips, so the
// nominal cadence stays 9s (any gate in (6000, 9000] does — this is the
// midpoint, equidistant from both failure edges).
const NO_WS_MESSAGES_IDLE_FETCH_GATE_MS =
  NO_WS_MESSAGES_IDLE_POLL_MS - NO_WS_MESSAGES_ACTIVE_POLL_MS / 2; // 7_500
const WS_SAFETY_MESSAGES_POLL_MS = 60_000;
// "Looks running": sidebar row status active, a local send/create/stop/compact
// within 60s (ticket: "sent-within-60s"), or a message row newer than 90s
// (matches PLANNER_STALE_CUTOFF_MS's view of a live stream).
const RECENT_ACTION_WINDOW_MS = 60_000;
const RECENT_MESSAGE_WINDOW_MS = 90_000;
// How long a WS-touched message key stays protected from snapshot-driven
// deletion in reconcileMessages. Backend /my message reads are
// eventually-consistent DynamoDB Queries, so a poll snapshot dispatched AFTER
// a WS delivery can still be missing that message — a fetch-window-scoped
// guard would let such a stale snapshot delete it. Protecting every key
// touched within this window of the fetch's dispatch covers DDB
// eventual-consistency staleness (typically <1s; 10s is a generous bound)
// while still letting genuine deletions converge on the next poll.
const WS_TOUCH_PROTECT_MS = 10_000;

// Extract the conversationId from a key shaped like "#message#{convId}#{msgId}".
function conversationIdFromMessageKey(key: string): string | null {
  const parts = key.split('#').filter(Boolean);
  if (parts.length >= 2 && parts[0] === 'message') {
    return parts[1];
  }
  return null;
}

// ENG-3112 / ENG-3879: WebSocket redelivery handling on the messages path.
//
// The ws-stream Lambda is a DynamoDB Streams consumer that broadcasts each
// record over the WebSocket fire-and-forget. Duplicates come from DynamoDB
// Streams retry/replay re-running the handler (not from any SQS
// BatchItemFailures mechanism), so the same INSERT/MODIFY can arrive one or
// more additional times. Duplicates are handled at these layers:
//
//   1. mergeMessage (below) keys on `item.key` — the DynamoDB sort key. A
//      redelivered event for an already-present message lands at the same index
//      and replaces the existing entry with an identical object, producing no
//      visible state change.
//
//   2. On the invalidation path (useRealtimeSubscription -> debouncedInvalidate
//      -> queryClient.invalidateQueries), TanStack Query's in-flight dedup
//      collapses multiple identical fetches for the same query key. The sidebar
//      list now lives on this path via useGetConversations.

// Control messages (e.g. the "stop" signal) are internal: the backend drains
// and deletes them, but the WS stream can surface the transient INSERT first.
// Keep them out of the displayed message state entirely.
function isControlMessage(msg: Message): boolean {
  return msg.role === 'control';
}

// HTTP-poll fallback for tutorial commands. The WS path intercepts
// eventName === 'tutorial_command' directly, but when no WebSocket is
// configured (local dev) or the WS event is missed, this detects
// trigger_tutorial tool-response messages arriving via HTTP polling and
// dispatches the same CustomEvent so TutorialProvider still activates.
//
// Dedup uses TWO sets: dispatchedTutorialKeys (by message DDB sort key, for
// the poll path) and dispatchedTutorialFingerprints (by content fingerprint,
// shared between WS and poll paths so a WS-delivered command suppresses the
// matching tool-response message on subsequent polls even after the time guard
// expires).
const dispatchedTutorialKeys = new Set<string>();
const dispatchedTutorialFingerprints = new Set<string>();
const initializedConversations = new Set<string>();

function tutorialFingerprint(
  action: string,
  stepId?: string,
  stepIds?: string[],
  tutorialId?: string
): string {
  return `${action}|${stepId || ''}|${stepIds?.join(',') || ''}|${tutorialId || ''}`;
}

// Mark all tutorial keys in a message batch as seen without dispatching.
// Called on the first fetch for a conversation so historical trigger_tutorial
// tool responses don't re-trigger tutorials when a conversation is reopened.
function seedTutorialKeysFromInitialLoad(messages: Message[]): void {
  for (const msg of messages) {
    if (msg.role !== 'tool response') continue;
    try {
      const parsed = JSON.parse(msg.content);
      if (parsed?.triggered === true && parsed?.action) {
        dispatchedTutorialKeys.add(msg.key);
        dispatchedTutorialFingerprints.add(
          tutorialFingerprint(
            parsed.action,
            parsed.step_id || parsed.stepId,
            parsed.step_ids || parsed.stepIds,
            parsed.tutorial_id || parsed.tutorialId
          )
        );
      }
    } catch {
      // not JSON — ignore
    }
  }
}

function dispatchTutorialFromMessage(msg: Message): void {
  if (msg.role !== 'tool response') return;
  if (dispatchedTutorialKeys.has(msg.key)) return;
  try {
    const parsed = JSON.parse(msg.content);
    if (parsed?.triggered === true && parsed?.action) {
      dispatchedTutorialKeys.add(msg.key);
      const fp = tutorialFingerprint(
        parsed.action,
        parsed.step_id || parsed.stepId,
        parsed.step_ids || parsed.stepIds,
        parsed.tutorial_id || parsed.tutorialId
      );
      if (dispatchedTutorialFingerprints.has(fp)) return;
      dispatchedTutorialFingerprints.add(fp);
      window.dispatchEvent(
        new CustomEvent('tutorial-command', {
          detail: {
            type: 'tutorial_command',
            action: parsed.action,
            stepId: parsed.step_id || parsed.stepId || '',
            stepIds: parsed.step_ids || parsed.stepIds || undefined,
            tutorialId: parsed.tutorial_id || parsed.tutorialId || undefined,
            message: parsed.message,
          },
        })
      );
    }
  } catch {
    // not JSON or not a tutorial response — ignore
  }
}

function mergeMessage(list: Message[], updated: Message): Message[] {
  if (isControlMessage(updated)) return list;
  const idx = list.findIndex(m => m.key === updated.key);
  const next =
    idx >= 0
      ? [...list.slice(0, idx), updated, ...list.slice(idx + 1)]
      : [...list, updated];
  return next.sort(
    (a, b) =>
      (new Date(a.timestamp).getTime() || 0) -
      (new Date(b.timestamp).getTime() || 0)
  );
}

// WS tenant arrives as the raw DDB partition: "{account}" for tenant-public
// items, "{account}:{user}" for per-user items. Strip the user suffix so the
// guard compares against the impersonated account regardless of partition shape.
function tenantAccount(tenant: string): string {
  const colon = tenant.indexOf(':');
  return colon === -1 ? tenant : tenant.slice(0, colon);
}

function pendingMatchesRealMessage(
  pending: PendingMessage,
  real: Message
): boolean {
  if (real.role !== 'user') return false;
  if (real.id === pending.id || real.uuid === pending.id) return true;
  // Fallback: content match within 5s of pending send time
  if (real.content === pending.text) {
    const realTs = new Date(real.timestamp || 0).getTime();
    if (Math.abs(realTs - pending.timestamp) < 5000) return true;
  }
  return false;
}

// Per-conversation in-flight fetch counters (ENG-4506 review round 2). The
// decrement deletes the entry at zero so the map stays bounded (same
// prune-in-place idiom as the wsTouched map).
function incInFlight(map: Map<string, number>, convId: string): void {
  map.set(convId, (map.get(convId) ?? 0) + 1);
}

function decInFlight(map: Map<string, number>, convId: string): number {
  const next = (map.get(convId) ?? 0) - 1;
  if (next <= 0) {
    map.delete(convId);
    return 0;
  }
  map.set(convId, next);
  return next;
}

// Returns true for conversations that belong in the root sidebar — i.e. root
// conversations that are not hunt iterations.  Both guards are needed:
//   • parent_id check: excludes subagent conversations (non-root children)
//   • huntId check: excludes hunt iteration conversations (root but hunt-scoped)
function isSidebarConversation(conv: Conversation): boolean {
  return (!conv.parent_id || conv.parent_id === 'self') && !conv.huntId;
}

export function useChatWorker(conversationId?: string): ChatWorkerState {
  const axios = useAxios();
  const { isImpersonating, friend, me } = useAuth();
  const currentUser = isImpersonating ? friend : me;

  // ENG-4506 / SEC-6: call-time principal for dispatch-vs-commit checks in the
  // fetch paths (they are useCallbacks that must not re-create on auth churn).
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  // Conversations and messages live in the per-user partition (user: true),
  // the same partition the chat WS subscriptions use. Track connection state so
  // we can resync after a reconnect (see the false->true effect below).
  const { isConnected } = useWebSocket({ user: true });
  const { wsConfigured, hasEverConnected } = useWsLiveness({ user: true });

  // ENG-4382: render-time overlay for just-created / just-compacted sidebar
  // conversations. The optimistic cache prepend (prependConversationToCache)
  // alone cannot make the new row stick: it no-ops while the list's FIRST
  // fetch is still in flight (cold create), and any refetch that read the
  // still-lagging parent_id:self GSI REPLACES the page without the row (warm
  // race) — react-query replaces page data on fetch resolution, it does not
  // merge, so every cache-write fix races the GSI. Instead, hold the
  // server-returned conversation locally and union it into the rendered list
  // at render time: no cache write, nothing for a stale fetch to erase. Once
  // the authoritative row lands in the query data, dedup-by-key hands the
  // render over to it seamlessly; the map entry itself is evicted after
  // OVERLAY_TTL_MS (see the eviction note below).
  // Each entry records the principal that created it. The union below filters
  // on `owner === currentUser` SYNCHRONOUSLY during render — never via an
  // effect — so an impersonation flip can't leak a pre-flip row into the new
  // principal's sidebar for even one committed frame (empty-never-foreign;
  // effects run post-commit, so an effect-based clear would be one frame too
  // late — Codex finding on PR #6709). Entries survive the flip: flipping back
  // within the TTL re-shows the original principal's still-live row.
  //
  // Declared BEFORE useGetConversations (below) so removeOverlayConversation can
  // be injected into that hook's #conversation# subscription (the
  // onConversationRemoved wiring). Nothing in this block reads
  // `conversations`/`sidebarConversations` — it depends only on `currentUser` —
  // so hoisting it above the list hook is safe and changes no behavior.
  const [overlayConversations, setOverlayConversations] = useState<
    Map<string, { conv: Conversation; owner: string | undefined }>
  >(() => new Map());
  const overlayTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  const removeOverlayConversation = useCallback((key: string) => {
    const timer = overlayTimersRef.current.get(key);
    if (timer) {
      clearTimeout(timer);
      overlayTimersRef.current.delete(key);
    }
    setOverlayConversations(prev => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const addOverlayConversation = useCallback(
    (conv: Conversation) => {
      if (!conv.key) return;
      setOverlayConversations(prev => {
        const next = new Map(prev);
        next.set(conv.key, { conv, owner: currentUser });
        return next;
      });
      const existing = overlayTimersRef.current.get(conv.key);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(
        () => removeOverlayConversation(conv.key),
        OVERLAY_TTL_MS
      );
      overlayTimersRef.current.set(conv.key, timer);
    },
    [removeOverlayConversation, currentUser]
  );

  // The sidebar conversation list is sourced from the canonical TanStack hook
  // (Option A). useChatWorker keeps its public `conversations` field but no
  // longer owns list state or merges WS events into it — useGetConversations
  // self-heals via #conversation# invalidation + 60s poll.
  const {
    data: conversations,
    updateAllSubQueries: updateConversationsCache,
    invalidate: invalidateConversations,
    isLoading: conversationsLoading,
    error: conversationsError,
    hasNextPage: hasMoreConversations,
    isFetchingNextPage: isLoadingMoreConversations,
    fetchNextPage: fetchNextConversationsPage,
  } = useGetConversations({
    // Evict a just-created overlay row the instant a #conversation# REMOVE for
    // it lands on the list subscription (a remote delete from another
    // tab/session, or a backend prune) instead of waiting out OVERLAY_TTL_MS —
    // the local useConversationManager tombstone only covers locally-initiated
    // deletes. Eviction by key is fail-safe (it can only HIDE a row, and
    // conversation keys are principal-unique UUIDs), so no tenant guard is
    // needed on this call.
    onConversationRemoved: removeOverlayConversation,
  });
  const [messages, setMessages] = useState<Message[]>([]);
  // ENG-4506: mirror for dispatch-time snapshots (pattern:
  // useInteractions.interactionsRef). fetchMessages captures beforeKeys from
  // this ref SYNCHRONOUSLY, before its first await.
  const messagesRef = useRef<Message[]>(messages);
  messagesRef.current = messages;
  // ENG-4506: per-conversation in-flight fetchMessages counts (counter, not
  // boolean — safety-net bursts and the poll overlap by design). Keyed by
  // convId so a stalled fetch for conversation A can never suppress B's poll
  // ticks or pin B's messagesLoading (the hook instance persists across
  // conversation switches). Used to skip poll ticks for the CURRENT
  // conversation (dedup) and to counter-gate the messagesLoading reset.
  const messagesFetchInFlightRef = useRef<Map<string, number>>(new Map());
  // Message key → epoch ms of the last WS handler / exact-key fetch mutation.
  // Recorded UNCONDITIONALLY (not just while a fetch is in flight): the poll
  // snapshot is an eventually-consistent DDB read, so it can be stale relative
  // to a WS delivery that happened BETWEEN polls — the commit path protects
  // every key touched within WS_TOUCH_PROTECT_MS of the fetch's dispatch and
  // prunes older entries in the same pass, so the map stays bounded.
  const wsTouchedDuringFetchRef = useRef<Map<string, number>>(new Map());
  const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Eviction fires on TWO triggers — a per-entry TTL AND an explicit
  // #conversation# REMOVE event (wired via onConversationRemoved above) — but
  // deliberately NOT "when the row appears in the query list" (key-presence).
  // Two reasons key-presence is wrong, both verified in
  // useChatWorker.warmRace.test.tsx:
  //  1. Key-presence can't distinguish the authoritative row from our own
  //     best-effort cache prepend (below): the prepend puts the row into the
  //     query data at create time (and TanStack structural sharing clones it,
  //     so a reference check can't tell either). Presence-eviction therefore
  //     self-triggers at create and hands the row back to the GSI race — a
  //     stale refetch then erases the prepend with the overlay already gone,
  //     which is the exact vanish frame this overlay exists to prevent.
  //  2. Key-presence would also mis-fire on GSI flap: a fetch that momentarily
  //     includes the row followed by a lagging fetch that drops it again.
  // The REMOVE trigger is immune to both — it fires only on a genuine delete,
  // never on the create/refetch cycle — so it shortens a deleted row's lifetime
  // without reopening the vanish window.
  // While an entry lives, the union below dedups by key — the query-list copy
  // wins the render the moment it exists, so there is never a double row and
  // nothing user-visible changes at TTL expiry once the row has landed.
  // The TTL now bounds only the narrow residual case: a row deleted server-side
  // whose REMOVE never reaches this subscription (e.g. the socket was down at
  // delete time) would otherwise linger until expiry. Locally-initiated deletes
  // are already hidden by useConversationManager's tombstone set; a remote
  // delete that DOES deliver its REMOVE is now evicted immediately.

  // Render-time union: this principal's overlay rows that the query list
  // doesn't have yet, merged in the list's own ordering (created desc,
  // matching useGetConversations). Dedup by key — the query-list row wins the
  // render in which it first appears; the map entry lingers silently until
  // its TTL (see above). The owner filter is the synchronous
  // empty-never-foreign gate described at the map declaration.
  const sidebarConversations = useMemo(() => {
    if (overlayConversations.size === 0) return conversations;
    const listKeys = new Set(conversations.map(c => c.key));
    const extras = [...overlayConversations.values()]
      .filter(e => e.owner === currentUser && !listKeys.has(e.conv.key))
      .map(e => e.conv);
    if (extras.length === 0) return conversations;
    return [...extras, ...conversations].sort(
      (a, b) =>
        (new Date(b.created).getTime() || 0) -
        (new Date(a.created).getTime() || 0)
    );
  }, [conversations, overlayConversations, currentUser]);

  // ENG-4506: tick-time signals for the poll (refs, so ticks see fresh values
  // without re-arming the interval). Status comes from the sidebar list row
  // when derivable (same match predicate as AIAssistant's selectedConversation
  // lookup); subagent conversations aren't in the sidebar list, so the two
  // recency signals — and the ≤9s idle cadence — carry them.
  const lastLocalActionRef = useRef(0);
  const listRowStatusRef = useRef<string | undefined>(undefined);
  const lastMessageTsRef = useRef(0);
  listRowStatusRef.current = conversationId
    ? sidebarConversations.find(
        c =>
          c.uuid === conversationId ||
          c.id === conversationId ||
          c.key?.includes(conversationId)
      )?.status
    : undefined;
  lastMessageTsRef.current = messages.length
    ? new Date(messages[messages.length - 1].timestamp).getTime() || 0
    : 0;

  // Refs avoid re-subscribing WS handlers on every state change.
  const activeConvIdRef = useRef<string | undefined>(conversationId);
  activeConvIdRef.current = conversationId;
  const pendingByConvRef = useRef<Map<string, PendingMessage[]>>(new Map());
  const pendingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const safetyNetTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const syncPendingForActive = useCallback(() => {
    const convId = activeConvIdRef.current;
    if (!convId) {
      setPendingMessages([]);
      return;
    }
    setPendingMessages([...(pendingByConvRef.current.get(convId) || [])]);
  }, []);

  const removePending = useCallback(
    (convId: string, messageId: string) => {
      const existing = pendingByConvRef.current.get(convId) || [];
      const remaining = existing.filter(p => p.id !== messageId);
      if (remaining.length !== existing.length) {
        pendingByConvRef.current.set(convId, remaining);
        if (activeConvIdRef.current === convId) {
          syncPendingForActive();
        }
      }
      const timer = pendingTimersRef.current.get(messageId);
      if (timer) {
        clearTimeout(timer);
        pendingTimersRef.current.delete(messageId);
      }
    },
    [syncPendingForActive]
  );

  const prunePendingForMessage = useCallback(
    (real: Message, convId: string) => {
      const existing = pendingByConvRef.current.get(convId) || [];
      const remaining = existing.filter(
        p => !pendingMatchesRealMessage(p, real)
      );
      if (remaining.length !== existing.length) {
        // Clear any timers for pruned entries
        for (const p of existing) {
          if (!remaining.includes(p)) {
            const timer = pendingTimersRef.current.get(p.id);
            if (timer) {
              clearTimeout(timer);
              pendingTimersRef.current.delete(p.id);
            }
          }
        }
        pendingByConvRef.current.set(convId, remaining);
        if (activeConvIdRef.current === convId) {
          syncPendingForActive();
        }
      }
    },
    [syncPendingForActive]
  );

  const fetchMessages = useCallback(
    async (convId: string) => {
      // ENG-4506 / SEC-6: capture the principal at dispatch. Poll responses
      // carry no tenant field (unlike WS events), so trust lives entirely in
      // request context — the commit below re-checks the principal
      // synchronously and drops the response on mismatch, mirroring the WS
      // tenant guard and the overlay's owner filter (empty-never-foreign).
      const principalAtDispatch = currentUserRef.current;
      // ENG-4506: dispatch-time snapshot — only keys that existed NOW may be
      // authoritatively deleted by this fetch's snapshot (see
      // reconcileMessages). Taken before the first await on purpose.
      const beforeKeys = new Set(messagesRef.current.map(m => m.key));
      // Anchor of the wsTouched protection window: a WS touch newer than
      // dispatchedAt - WS_TOUCH_PROTECT_MS may be invisible to this fetch's
      // eventually-consistent snapshot and must survive the reconcile.
      const dispatchedAt = Date.now();
      incInFlight(messagesFetchInFlightRef.current, convId);
      try {
        setMessagesLoading(true);
        const allMessages: Message[] = [];
        let offset: any = undefined;

        do {
          const response = await axios.get('/my', {
            params: {
              label: 'message',
              key: `#message#${convId}`,
              user: 'true',
              offset: offset ? JSON.stringify(offset) : undefined,
            },
          });
          const page = ((response.data.messages || []) as Message[]).filter(
            m => !isControlMessage(m)
          );
          allMessages.push(...page);
          offset = response.data.offset;

          // SEC-9 + SEC-6: per-page bail mirrors the final-commit check below
          // — a conversation switch OR principal flip mid-pagination abandons
          // the chain (partial snapshot must never drive a reconcile, and
          // post-flip pages must not be fetched under the new principal's
          // header for a pre-flip dispatch).
          if (
            activeConvIdRef.current !== convId ||
            currentUserRef.current !== principalAtDispatch
          ) {
            return;
          }
        } while (offset);

        // SEC-9 + SEC-6: explicit final-commit re-check of BOTH the active
        // conversation and the principal (the loop's per-page bail covers the
        // pagination window; this line covers the commit itself).
        if (
          activeConvIdRef.current !== convId ||
          currentUserRef.current !== principalAtDispatch
        ) {
          return;
        }

        // ENG-4506: snapshot-reconcile instead of the previous bare union —
        // converges deletions (beforeKeys guard) and never clobbers a row the
        // WS touched recently enough that this fetch's eventually-consistent
        // snapshot may not include it (wsTouched guard). The functional update
        // still protects against unrelated concurrent setMessages writers.
        // Snapshot the protected keys at commit time so the updater below
        // stays pure (no ref reads inside): concurrent touches between commit
        // and flush must not change what THIS reconcile protects. Entries
        // older than the cutoff are pruned in the same pass so the map cannot
        // grow unboundedly.
        const wsTouchCutoff = dispatchedAt - WS_TOUCH_PROTECT_MS;
        const wsTouchedSnapshot = new Set<string>();
        for (const [key, touchedAt] of wsTouchedDuringFetchRef.current) {
          if (touchedAt >= wsTouchCutoff) {
            wsTouchedSnapshot.add(key);
          } else {
            wsTouchedDuringFetchRef.current.delete(key);
          }
        }
        setMessages(prev =>
          reconcileMessages(prev, allMessages, beforeKeys, wsTouchedSnapshot)
        );
        // Prune any pending that already made it into the list
        if (!initializedConversations.has(convId)) {
          seedTutorialKeysFromInitialLoad(allMessages);
          initializedConversations.add(convId);
        }
        for (const m of allMessages) {
          prunePendingForMessage(m, convId);
          dispatchTutorialFromMessage(m);
        }
      } catch (err) {
        console.error('Failed to fetch messages', convId, err);
      } finally {
        const remaining = decInFlight(messagesFetchInFlightRef.current, convId);
        // Counter-gated reset: loading clears exactly when the LAST in-flight
        // chain FOR THIS conversation exits — success, throw, AND the abort
        // early-returns above (which previously left the flag stuck true).
        // The activeConvIdRef check makes the old cross-conversation race
        // exact instead of approximate: a late-finishing fetch for A can
        // neither clear nor pin B's just-set loading — only the conversation
        // that is still active clears its own flag, and B's own finally
        // clears B's.
        if (remaining === 0 && activeConvIdRef.current === convId) {
          setMessagesLoading(false);
        }
      }
    },
    [axios, prunePendingForMessage]
  );

  // Fetch a single message by its exact key and merge into state.
  const fetchAndMergeMessage = useCallback(
    async (key: string) => {
      const convId = conversationIdFromMessageKey(key);
      if (!convId) return;
      // Only bother if this is the active conversation — otherwise the next
      // conversation switch will do a full fetch anyway.
      if (convId !== activeConvIdRef.current) return;
      // ENG-4506 / SEC-6: dispatch-time principal, re-checked at commit below.
      const principalAtDispatch = currentUserRef.current;

      try {
        const response = await axios.get('/my', {
          params: { label: 'message', key, user: 'true' },
        });
        const list = (response.data.messages || []) as Message[];
        if (
          activeConvIdRef.current !== convId ||
          currentUserRef.current !== principalAtDispatch
        ) {
          return;
        }

        if (list.length === 0) {
          wsTouchedDuringFetchRef.current.set(key, Date.now());
          setMessages(prev => prev.filter(m => m.key !== key));
          return;
        }
        const updated = list[0];
        wsTouchedDuringFetchRef.current.set(updated.key, Date.now());
        setMessages(prev => mergeMessage(prev, updated));
        prunePendingForMessage(updated, convId);
      } catch (err) {
        console.error('Failed to fetch message', key, err);
      }
    },
    [axios, prunePendingForMessage]
  );

  // WS subscriptions are declared BEFORE the initial fetch effects so that
  // React runs the subscribe effects first on the same render cycle.  This
  // eliminates the window where a message written between fetch-response and
  // subscribe-activation would be missed.  Any duplicate delivered by WS
  // during the fetch is deduplicated by mergeMessage (keyed on item key).

  // Live message updates for the active conversation. The WS payload includes
  // the full item serialized via TableSearch (same shape as /my), so we can
  // merge directly into state without an HTTP round-trip.
  const handleMessageEvent = useCallback(
    (ws: WebSocketMessage) => {
      // Drop events before auth resolves — accepting them risks cross-tenant
      // state pollution. Initial data arrives via HTTP fetches; safety-net
      // refetches cover any WS events missed during this window.
      if (!currentUser) return;
      if (ws.tenant && tenantAccount(ws.tenant) !== currentUser) return;
      // Detect trigger_tutorial tool responses from DDB Stream INSERT events.
      // The tool result is stored as a conversation message; the DDB stream
      // broadcasts it as a regular INSERT — extract the tutorial command from
      // the message content and dispatch it immediately.
      if (ws.eventName === 'INSERT' || ws.eventName === 'MODIFY') {
        const wsMsg = (ws.item as Record<string, unknown> | undefined)
          ?.messages as Record<string, unknown>[] | undefined;
        const first = wsMsg?.[0];
        if (
          first?.toolName === 'trigger_tutorial' &&
          first?.role === 'tool response'
        ) {
          try {
            const parsed = JSON.parse(first.content as string);
            if (parsed?.triggered === true && parsed?.action) {
              const fp = tutorialFingerprint(
                parsed.action,
                parsed.step_id || parsed.stepId,
                parsed.step_ids || parsed.stepIds,
                parsed.tutorial_id || parsed.tutorialId
              );
              if (!dispatchedTutorialFingerprints.has(fp)) {
                dispatchedTutorialFingerprints.add(fp);
                window.dispatchEvent(
                  new CustomEvent('tutorial-command', {
                    detail: {
                      type: 'tutorial_command',
                      action: parsed.action,
                      stepId: parsed.step_id || parsed.stepId || '',
                      stepIds: parsed.step_ids || parsed.stepIds || undefined,
                      tutorialId:
                        parsed.tutorial_id || parsed.tutorialId || undefined,
                      message: parsed.message,
                    },
                  })
                );
              }
            }
          } catch {
            // not JSON — fall through to regular message processing
          }
        }
      }
      if (ws.eventName === 'REMOVE') {
        wsTouchedDuringFetchRef.current.set(ws.key, Date.now());
        setMessages(prev => prev.filter(m => m.key !== ws.key));
        return;
      }
      const convId = conversationIdFromMessageKey(ws.key);
      if (!convId || convId !== activeConvIdRef.current) return;

      // Extract the message from the TableSearch-shaped item payload.
      const messages = (ws.item?.messages ?? []) as Message[];
      if (messages.length > 0) {
        const msg = messages[0];
        wsTouchedDuringFetchRef.current.set(msg.key, Date.now());
        setMessages(prev => mergeMessage(prev, msg));
        prunePendingForMessage(msg, convId);
        return;
      }
      // Fallback: fetch if the item wasn't included (e.g. older backend).
      fetchAndMergeMessage(ws.key);
    },
    [fetchAndMergeMessage, prunePendingForMessage, currentUser]
  );
  useRealtimeSubscription({
    patterns: conversationId
      ? [{ pattern: `#message#${conversationId}`, matchType: 'prefix' }]
      : [],
    enabled: !!conversationId,
    onUpdate: handleMessageEvent,
    // Subscribe rejected (e.g. MaxSubsPerConnection) → no live message events.
    // Fetch once so messages still flow for this conversation; no resubscribe.
    onError: reason => {
      console.error('useChatWorker: message subscribe rejected', {
        reason,
        conversationId,
      });
      const convId = activeConvIdRef.current;
      if (convId) void fetchMessages(convId);
    },
  });

  // Initial fetches run AFTER subscriptions are established above.
  useEffect(() => {
    if (conversationId) {
      setMessages([]);
      syncPendingForActive();
      fetchMessages(conversationId);
    } else {
      setMessages([]);
      setPendingMessages([]);
      setMessagesLoading(false);
    }
  }, [conversationId, fetchMessages, syncPendingForActive]);

  // Reconnect resync: any #conversation# / #message# events broadcast while the
  // socket was down are never redelivered (the ws-stream broadcaster is
  // fire-and-forget). On a false->true transition:
  //  - invalidate the conversations list so a status flip (e.g. active->idle)
  //    missed during the outage is recovered now, rather than waiting up to the
  //    60s fallback poll. selectedConversation is merged list-row-first
  //    (see mergeSelectedConversation), so a stale-'active' list row would
  //    otherwise wedge the composer's Send/Stop gate until the poll. This
  //    mirrors the pre-migration worker, which refetched the list on reconnect.
  //  - refetch the open conversation's messages to recover missed #message#
  //    events.
  const prevConnectedRef = useRef(isConnected);
  useEffect(() => {
    const reconnected = !prevConnectedRef.current && isConnected;
    prevConnectedRef.current = isConnected;
    if (!reconnected || !currentUser) return;
    invalidateConversations();
    const convId = activeConvIdRef.current;
    if (convId) {
      void fetchMessages(convId);
    }
  }, [isConnected, currentUser, fetchMessages, invalidateConversations]);

  // ENG-4506: passive liveness fallback for the messages pane. Two modes:
  //  - FALLBACK (no WS URL, or the socket has NEVER connected): the WS merge
  //    path is structurally dead, so this poll is the message stream — 3s
  //    while the conversation looks running, every third tick (9s) when idle.
  //    Gated on the hasEverConnected LATCH, never on isConnected, so a
  //    healthy reconnect flicker cannot engage it.
  //  - SAFETY NET (WS has connected at least once): 60s poll only while the
  //    conversation looks running, regardless of current socket state —
  //    covers a zombie socket that still looks connected but delivers
  //    nothing (no pong watchdog exists; that is its own ticket).
  // Ticks self-gate (visibility, in-flight fetch, likely-running) so the
  // interval is steady and deterministic under fake timers. Merges are safe
  // against concurrent WS writes via reconcileMessages' guards.
  useEffect(() => {
    // SEC-3 + SEC-7: keyed on conversation AND principal — teardown on
    // unmount, conversation switch, and impersonation flip, so no timer ever
    // fires with a pre-flip closure.
    if (!conversationId || !currentUser) return;
    const fallbackMode = !wsConfigured || !hasEverConnected;
    const baseMs = fallbackMode
      ? NO_WS_MESSAGES_ACTIVE_POLL_MS
      : WS_SAFETY_MESSAGES_POLL_MS;
    // The mount/switch effect has just fetched; start the idle clock now.
    let lastPollFetch = Date.now();

    const likelyRunning = () => {
      const now = Date.now();
      return (
        listRowStatusRef.current === 'active' ||
        now - lastLocalActionRef.current < RECENT_ACTION_WINDOW_MS ||
        (lastMessageTsRef.current > 0 &&
          now - lastMessageTsRef.current < RECENT_MESSAGE_WINDOW_MS)
      );
    };

    const tick = () => {
      if (document.visibilityState === 'hidden') return; // background-tab pause
      // Never stack fetches FOR THIS conversation — per-conversation gate, so
      // a stalled fetch left over from another conversation can't starve this
      // one's poll (ENG-4506 review round 2).
      if ((messagesFetchInFlightRef.current.get(conversationId) ?? 0) > 0)
        return;
      const now = Date.now();
      if (fallbackMode) {
        if (
          !likelyRunning() &&
          now - lastPollFetch < NO_WS_MESSAGES_IDLE_FETCH_GATE_MS
        ) {
          return; // idle: relax to the 9s cadence
        }
      } else if (!likelyRunning()) {
        return; // safety net spends traffic only on a running conversation
      }
      lastPollFetch = now;
      void fetchMessages(conversationId);
    };

    const interval = setInterval(tick, baseMs);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tick(); // refocus catch-up
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [
    conversationId,
    currentUser,
    wsConfigured,
    hasEverConnected,
    fetchMessages,
  ]);

  // Shared safety-net refetch helper. Schedules staggered fetchMessages calls
  // to catch AI responses that a WebSocket subscription timing race may have
  // missed.
  function scheduleSafetyNetRefetches(
    convId: string,
    delays: number[]
  ): ReturnType<typeof setTimeout>[] {
    const timers = delays.map(delay => {
      const timer = setTimeout(() => {
        safetyNetTimersRef.current = safetyNetTimersRef.current.filter(
          t => t !== timer
        );
        if (activeConvIdRef.current === convId) {
          void fetchMessages(convId);
        }
      }, delay);
      return timer;
    });
    safetyNetTimersRef.current.push(...timers);
    return timers;
  }

  const sendMessage = useCallback(
    async (convId: string, text: string, mode?: 'query' | 'agent') => {
      lastLocalActionRef.current = Date.now();
      const messageId = crypto.randomUUID();
      const pending: PendingMessage = {
        id: messageId,
        text,
        timestamp: Date.now(),
      };
      const list = pendingByConvRef.current.get(convId) || [];
      const nextList = [...list, pending];
      pendingByConvRef.current.set(convId, nextList);
      if (activeConvIdRef.current === convId) {
        syncPendingForActive();
      }

      // Safety: drop stuck pending entry after timeout so the UI doesn't
      // show "sending..." forever if something goes sideways.
      const timer = setTimeout(
        () => removePending(convId, messageId),
        PENDING_TIMEOUT_MS
      );
      pendingTimersRef.current.set(messageId, timer);

      try {
        const response = await axios.post('/planner', {
          conversationId: convId,
          message: text,
          mode: mode || 'query',
          messageId,
        });
        if (!response.data?.response?.success) {
          throw new Error('Message failed to send');
        }
        scheduleSafetyNetRefetches(convId, [2_000, 5_000, 12_000]);
      } catch (err) {
        removePending(convId, messageId);
        throw err;
      }
    },
    [axios, removePending, syncPendingForActive, fetchMessages]
  );

  const createConversation = useCallback(
    async (message: string, mode?: 'query' | 'agent') => {
      lastLocalActionRef.current = Date.now();
      const response = await axios.post('/planner', {
        conversationId: '',
        message,
        mode: mode || 'query',
      });
      if (!response.data?.conversation) {
        throw new Error('No conversation returned from server');
      }
      const conv = response.data.conversation;

      if (isSidebarConversation(conv)) {
        // ENG-4382: render-time overlay shows the new (and about-to-be-active)
        // conversation in the sidebar instantly, across BOTH GSI-lag windows
        // (cold: list's first fetch unresolved; warm: a stale invalidate/poll
        // refetch replacing the cache without the row).
        addOverlayConversation(conv);
        // Best-effort prepend into the TanStack conversations cache so the
        // durable snapshot converges without waiting for the next refetch.
        // No-ops on an unprimed cache and is erased by a stale GSI refetch —
        // the overlay covers those windows. The debounced #conversation# WS
        // invalidate in useGetConversations reconciles the durable snapshot;
        // useMy's (username,key) dedup collapses any transient double. See
        // prependConversationToCache.
        updateConversationsCache(
          old => prependConversationToCache(old, conv) ?? old
        );
      }

      const convId =
        conv.uuid || conv.id || (conv.key || '').replace('#conversation#', '');

      if (convId) {
        // Optimistic pending message so the user's text is visible in the
        // new conversation before any WS event or safety-net refetch arrives.
        const messageId = crypto.randomUUID();
        const pending: PendingMessage = {
          id: messageId,
          text: message,
          timestamp: Date.now(),
        };
        pendingByConvRef.current.set(convId, [pending]);
        const pendingTimer = setTimeout(
          () => removePending(convId, messageId),
          PENDING_TIMEOUT_MS
        );
        pendingTimersRef.current.set(messageId, pendingTimer);

        scheduleSafetyNetRefetches(
          convId,
          [2_000, 5_000, 12_000, 20_000, 30_000]
        );
      }

      return conv;
    },
    [
      axios,
      updateConversationsCache,
      addOverlayConversation,
      fetchMessages,
      removePending,
    ]
  );

  // Ask a running agent to stop. The backend writes a "stop" control message
  // to the inbox; the loop drains it, finalizes its partial result, and goes
  // idle (the conversation status flips via the usual WS fan-out). The user's
  // next message then re-invokes the planner fresh.
  const stopConversation = useCallback(
    async (convId: string) => {
      lastLocalActionRef.current = Date.now();
      await axios.post('/planner/stop', { conversationId: convId });
      scheduleSafetyNetRefetches(convId, [1_000, 3_000, 8_000]);
    },
    [axios]
  );

  // Cleanup pending, safety-net, and overlay-TTL timers on unmount.
  useEffect(() => {
    return () => {
      for (const timer of pendingTimersRef.current.values()) {
        clearTimeout(timer);
      }
      pendingTimersRef.current.clear();
      for (const timer of safetyNetTimersRef.current) {
        clearTimeout(timer);
      }
      safetyNetTimersRef.current = [];
      for (const timer of overlayTimersRef.current.values()) {
        clearTimeout(timer);
      }
      overlayTimersRef.current.clear();
    };
  }, []);

  // Async compaction. The server creates the target conversation synchronously
  // and enqueues an SQS event to do the actual summarization (which can take
  // longer than the API gateway's 30s window). The summary message arrives in
  // the new conversation later via the standard WebSocket fan-out.
  const compactConversation = useCallback(
    async (sourceConversationId: string) => {
      lastLocalActionRef.current = Date.now();
      const response = await axios.post('/planner/compact', {
        conversationId: sourceConversationId,
      });
      if (!response.data?.conversation) {
        throw new Error('No conversation returned from compaction');
      }
      const conv = response.data.conversation;

      if (isSidebarConversation(conv)) {
        // ENG-4382: same render-time overlay + best-effort cache prepend as
        // createConversation above — see that comment for the two GSI-lag
        // windows the overlay covers.
        addOverlayConversation(conv);
        updateConversationsCache(
          old => prependConversationToCache(old, conv) ?? old
        );
      }

      const convId =
        conv.uuid || conv.id || (conv.key || '').replace('#conversation#', '');
      if (convId) {
        // Compaction takes longer than the API timeout — refetch on a longer
        // schedule so the summary message lands without waiting for a WS event.
        scheduleSafetyNetRefetches(
          convId,
          [3_000, 8_000, 20_000, 45_000, 90_000]
        );
      }

      return conv;
    },
    [axios, updateConversationsCache, addOverlayConversation]
  );

  // ENG-4398: expose the pagination trigger with a stable void signature.
  // Fire-and-forget — failures surface through the query's error state, and
  // #conversation# invalidations refetch every loaded page anyway.
  const loadMoreConversations = useCallback(() => {
    void fetchNextConversationsPage();
  }, [fetchNextConversationsPage]);

  return {
    conversations: sidebarConversations,
    messages,
    pendingMessages,
    conversationsLoading,
    hasMoreConversations,
    isLoadingMoreConversations,
    loadMoreConversations,
    messagesLoading,
    error: conversationsError
      ? ((conversationsError as Error).message ?? null)
      : null,
    sendMessage,
    createConversation,
    compactConversation,
    stopConversation,
    refetchMessages: fetchMessages,
  };
}
